import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { FaUserPlus, FaTrash, FaDownload, FaShieldAlt, FaUserShield, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

export default function ManageAdmins() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'admin' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdmins();
    }
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await API.get('/admins');
      setAdmins(res.data);
    } catch { toast.error('Failed to load admins'); }
    finally { setLoading(false); }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!form.username || !form.email || !form.password) return toast.error('All fields required');
    setSubmitting(true);
    try {
      await API.post('/admins', form);
      toast.success('Admin added successfully!');
      setShowModal(false);
      setForm({ username: '', email: '', password: '', role: 'admin' });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add admin');
    } finally { setSubmitting(false); }
  };

  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;
    try {
      await API.delete(`/admins/${adminToDelete.id}`);
      toast.success('Admin removed');
      setAdminToDelete(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove');
    }
  };

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div>
      {/* Topbar */}
      <div className="topbar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>Manage Admins</h1>
          <p style={{ fontSize: 13, color: '#6b7280' }}>{dateStr}</p>
        </div>
        <div>
          {isSuperAdmin && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <FaUserPlus size={14} /> Add New Admin
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <div>
              <h3 style={{ fontWeight: 700, fontSize: 16 }}>System Administrators</h3>
              <p style={{ fontSize: 13, color: '#6b7280' }}>Manage admin accounts and their access.</p>
            </div>
          </div>

          <div className="table-container">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
                <div className="spinner"></div>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Created At</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map(a => (
                    <tr key={a.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: '50%',
                            background: 'linear-gradient(135deg, #15803d, #22c55e)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontWeight: 700, fontSize: 14
                          }}>
                            {a.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{a.username}</div>
                            {a.id === user?.id && <div style={{ fontSize: 11, color: '#16a34a' }}>You</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ color: '#6b7280' }}>{a.email}</td>
                      <td>
                        <span className={`badge ${a.role === 'superadmin' ? 'badge-success' : 'badge-blue'}`}>
                          {a.role === 'superadmin' ? '⭐ Super Admin' : '🛡️ Admin'}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>
                        {a.created_at ? new Date(a.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>
                        {a.last_login ? new Date(a.last_login).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : 'Never'}
                      </td>
                      <td>
                        {a.role === 'superadmin' ? (
                          <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>Super Admin (Protected)</span>
                        ) : isSuperAdmin && a.id !== user?.id ? (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => setAdminToDelete(a)}
                            style={{ padding: '5px 12px' }}
                          >
                            <FaTrash size={12} /> Remove
                          </button>
                        ) : a.id === user?.id ? (
                          <span style={{ fontSize: 12, color: '#9ca3af' }}>Current User</span>
                        ) : (
                          <span style={{ fontSize: 12, color: '#9ca3af' }}>Admin</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Add Admin Modal (Super Admin only, strictly creates standard Admin) */}
      {showModal && isSuperAdmin && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, background: '#f0fdf4', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaUserShield color="#16a34a" />
                </div>
                <h3 style={{ fontWeight: 700 }}>Add New Admin</h3>
              </div>
              <button onClick={() => setShowModal(false)} style={{ border: 'none', background: '#f3f4f6', width: 30, height: 30, borderRadius: 8, cursor: 'pointer' }}>✕</button>
            </div>
            <form onSubmit={handleAddAdmin}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Username <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="form-input" placeholder="e.g. john_admin" value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="form-input" type="email" placeholder="admin@karunasai.org" value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="form-input" type="password" placeholder="Min 6 characters" value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })} required minLength={6} />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <input
                    className="form-input"
                    value="Standard Admin"
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
                  />
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    Only one Super Admin is permitted in the system.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Adding...' : <><FaUserPlus size={13} /> Add Admin</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Admin Confirmation Modal */}
      {adminToDelete && (
        <div className="modal-overlay" onClick={() => setAdminToDelete(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, background: '#fee2e2', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaExclamationTriangle color="#ef4444" size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>Remove Admin</h3>
                  <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Confirm administrator removal</p>
                </div>
              </div>
              <button
                onClick={() => setAdminToDelete(null)}
                style={{ border: 'none', background: '#f3f4f6', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 16 }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body" style={{ paddingTop: 16 }}>
              <p style={{ fontSize: 14, color: '#374151', margin: 0 }}>
                Are you sure you want to remove administrator:
              </p>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginTop: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#991b1b' }}>{adminToDelete.username}</div>
                <div style={{ fontSize: 13, color: '#b91c1c' }}>{adminToDelete.email}</div>
                <div style={{ fontSize: 12, color: '#7f1d1d', textTransform: 'capitalize', marginTop: 2 }}>Role: {adminToDelete.role}</div>
              </div>
            </div>
            <div className="modal-footer" style={{ borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setAdminToDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmDelete}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <FaTrash size={12} /> Remove Admin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
